<template>
  <draggable
    v-bind="dragOptions"
    class="draggable-area"
    :class="{ 'has-items': realValue.length > 0 }"
    :disabled="isDisabled"
    :list="list"
    :value="value"
    :sort="isSortable"
    @input="emitter"
  >
    <!-- Key must be unique and cannot change when state changes -->
    <div
      v-for="(item, index) in realValue"
      :key="item.id"
      class="list-group"
    >
      <div
        class="list-item"
        :level="level"
      >
        <div class="list-item__summary">
          <div
            v-if="isSortable"
            class="list-item__handler"
            @mousedown="closeEdit"
          >
            <IconDrag size="xs" />
          </div>
          <div
            class="list-item__title"
            @click="edit(getItemPath(index))"
          >
            <span v-html="getItemLabel(item, index)" />
          </div>
          <div class="list-item__actions">
            <button
              class="edit-btn text-primary"
              title="Editar item da lista"
              :disabled="isDisabled"
              @click="edit(getItemPath(index))"
            >
              <IconEdit size="xs" />
            </button>
            <button
              class="remove-btn text-danger"
              title="Excluir item da lista"
              :disabled="
                isDisabled ||
                (level === 1 && minItems && value.length <= minItems)
              "
              @click="remove(index)"
            >
              <IconDelete size="xs" />
            </button>
          </div>
        </div>
        <div
          v-show="getItemPath(index) === itemBeingEdited"
          class="list-item__editor"
        >
          <component
            v-bind="field"
            :is="UiTypes[field.type]"
            v-for="field in fields"
            v-show="checkConditionalVisibility(field, index)"
            :key="`${field.name}-${item.id}`"
            :name="getRepeatableName(field, index)"
          />
        </div>
      </div>
      <ListItem
        v-if="allowSubitem"
        class="list-group--sub"
        :name="`${name}.${index}.${name}`"
        :parent-name="subitems"
        :list="item[subitems]"
        :level="level + 1"
        :fields="fields"
        :max-level="maxLevels"
        :item-label="itemLabel"
        :item-being-edited="itemBeingEdited"
        :is-disabled="isDisabled"
        @edit-item="edit"
      />
    </div>
  </draggable>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import draggable from 'vuedraggable';

import * as Modules from '@/modules';
import GlobalDataModules from '@/modules/global-data';
import ModalModules from '@/modules/modals';
import IconDrag from '@/components/icons/IconDrag.vue';
import IconEdit from '@/components/icons/IconEdit.vue';
import IconDelete from '@/components/icons/IconDelete.vue';
import replaceTokens from '@/helpers/replaceTokens';
import extendMetadata from '@/helpers/extendMetadata';
import getMetadataFields from '@/helpers/getMetadataFields';
import * as UiTypes from '@/ui-types';

export default {
  name: 'ListItem',
  components: {
    draggable,
    IconDrag,
    IconEdit,
    IconDelete,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    parentName: {
      type: String,
      default: '',
    },
    level: {
      type: Number,
      default: 1,
    },
    value: {
      type: Array,
      default: null,
    },
    list: {
      type: Array,
      default: null,
    },
    itemBeingEdited: {
      type: String,
      default: '',
    },
    fields: {
      type: Array,
      default: () => [],
    },
    maxLevels: {
      type: Number,
      default: 1,
    },
    minItems: {
      type: Number,
      default: null,
    },
    maxItems: {
      type: Number,
      default: undefined,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isSortable: {
      type: Boolean,
      default: true,
    },
    itemLabel: {
      type: [String, Function],
      default: 'Item nº {index}',
    },
  },
  data() {
    return {
      UiTypes,
    };
  },
  computed: {
    subitems() {
      return this.parentName || this.name;
    },
    dragOptions() {
      return {
        animation: 200,
        handle: '.list-item__handler',
        group: 'list',
        swapThreshold: 0.6,
        move: (event) => {
          // TODO: Replicate moved property here
          const { dragged, to } = event;
          const hasChildBeignDragged =
            dragged.querySelector('.list-group') !== null;
          const newLevelIsSub = to.classList.contains('list-group--sub');

          // Do not allow a item with subitem to become subitem (would create a 3-level list)
          // TODO: Consider max level props on this
          if (hasChildBeignDragged && newLevelIsSub) {
            return false; // cancel move
          }
          return true;
        },
      };
    },
    realValue() {
      return this.value || this.list;
    },
    allowSubitem() {
      return this.level < this.maxLevels;
    },
    // Moved from instance properties. Propably shouldn't be here
    activeEditableGroup() {
      return this.activeGlobalData ?? this.activeInstance;
    },
    instance() {
      if (this.activeGlobalData) {
        return this.$store.getters.globalData(this.activeEditableGroup);
      }
      // TODO: Refact instance property names to use instance from mapGetters
      return this.$store.getters.instance(this.activeEditableGroup);
    },
    moduleList() {
      if (this.activeGlobalData) {
        return GlobalDataModules;
      }
      if (this.container.type === 'modals') {
        return ModalModules;
      }
      return Modules;
    },
    module() {
      return this.moduleList[this.instance.module];
    },
    metadata() {
      return extendMetadata(this.module.metadata);
    },
    metaDataKeyValue() {
      const allFields = getMetadataFields(this.metadata);
      const metaDataKeyValue = {};

      allFields.forEach(({ name }) => {
        metaDataKeyValue[name] = this.instance.data[name];
      });

      return metaDataKeyValue;
    },
    ...mapState(['activeGlobalData', 'activeInstance', 'customerSettings']),
    ...mapGetters([
      'activePage',
      'container',
      'hasLetsbookIntegration',
      'hasCRMIntegration',
      'activeInstanceThis',
    ]),
  },
  methods: {
    emitter(value) {
      this.$emit('input', value);
    },
    edit(itemPath) {
      if (this.itemBeingEdited === itemPath) {
        this.closeEdit();
      } else {
        this.$emit('edit-item', itemPath);
      }
    },
    remove(indexToRemove) {
      this.realValue.splice(indexToRemove, 1);

      if (
        !!this.$store.getters.containerId &&
        !this.$store.getters.activeGlobalData &&
        !this.$store.getters.activeExternalGlobalInstance
      ) {
        this.$store.dispatch('saveActiveContainer');
      }
    },
    closeEdit() {
      this.$emit('edit-item', '');
    },
    getItemPath(index) {
      return `${this.name}.${index}`;
    },
    getItemLabel(item, index) {
      if (typeof this.itemLabel === 'function') {
        const label = this.itemLabel.apply(this.activeInstanceThis, [item]);
        return replaceTokens(label, {
          ...item,
          index: index + 1,
        });
      }

      const label = replaceTokens(this.itemLabel, {
        ...item,
        index: index + 1,
      });

      return label || 'Sem nome';
    },
    getRepeatableName(field, index) {
      return `${this.name}.${index}.${field.name}`;
    },
    checkConditionalVisibility({ isEnabled }, index) {
      if (isEnabled && typeof isEnabled === 'function') {
        // TODO: Use lodash get so it works with subitems
        return isEnabled.call(
          { ...this.metaDataKeyValue[this.name][index] },
          {
            ...this.metaDataKeyValue,
            ...this.customerSettings,
            instance: this.instance,
            page: this.activePage || {},
            hasLetsbookIntegration: this.hasLetsbookIntegration,
            hasCRMIntegration: this.hasCRMIntegration,
          },
        );
      }
      return true;
    },
  },
};
</script>

<style scoped lang="scss">
.list-group {
  position: relative;

  &--sub {
    position: relative;
    margin-left: 24px;

    $horizontalLineWidth: 10px;

    // vertical line on subgroups
    &.has-items:before {
      content: '';
      position: absolute;
      background: $gray-200;
      width: 1px;
      height: calc(100% + 8px); // .list-item's margin-bottom
      top: -8px;
      left: -$horizontalLineWidth;
    }

    // horizontal line on subitems
    .list-group:before {
      content: '';
      position: absolute;
      background: $gray-200;
      width: $horizontalLineWidth;
      height: 1px;
      top: 16px;
      left: -$horizontalLineWidth;
    }
  }
}

.list-item {
  margin-bottom: 8px;

  &__summary {
    display: flex;
    width: 100%;
    background: $lighter;
    border: 1px solid $gray-200;
    border-radius: 24px;
    padding: 6px;
  }

  &__handler {
    display: flex;
    align-items: center;
    padding-left: 8px;
    cursor: grab;

    .icon--xs {
      height: 16px;
      width: 16px;
    }

    svg {
      width: 9px;
      display: block;

      circle {
        fill: #c2c2c2;
      }
    }
  }

  &__title {
    flex-grow: 1;
    display: flex;
    align-items: center;
    color: $crm-blue-primary;
    font-size: $size-small;
    cursor: pointer;
    margin-left: 10px;
    min-width: 0; // @ https://css-tricks.com/flexbox-truncated-text/

    span {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }

  &__actions {
    display: flex;
    flex-shrink: 0;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 6px;
      margin-left: 3px;
      background: $gray-200;
      border-radius: 50%;
      cursor: pointer;

      &[disabled] {
        cursor: not-allowed;
      }

      &.edit-btn:hover {
        background: $crm-blue-primary;

        ::v-deep svg path {
          fill: $white;
        }
      }

      &.remove-btn:hover {
        background: $crm-red-primary;

        ::v-deep svg path {
          fill: $white;
        }
      }
    }
  }

  &__editor {
    border: 1px solid $gray-200;
    border-top: none;
    margin: 0 12px;
    padding: 21px 16px;

    ::v-deep {
      .field {
        margin: 0;

        &:not(:last-child),
        .select-label {
          margin-bottom: 24px;
        }

        .help-text {
          margin-top: 0 !important;
        }
      }

      .form-control {
        height: auto;
      }

      .custom-checkbox {
        .custom-control-label {
          cursor: pointer;

          &:before,
          &:after {
            width: 16px;
            height: 16px;
          }
        }
      }
    }
  }
}
</style>
